import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Variante {
  id: number;
  tipo?: string;
  cor: string;
  tamanho: string;
  sku: string;
  estoque: number;
  ativo: boolean;
}

interface Produto {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  categorias: number[];
  fotos: { id: number; url: string; principal: boolean }[];
  variantes: Variante[];
  atasIds: number[];
}

interface ProductCardProps {
  product: Produto;
  categoryNames: string[];
  atasNomes: string[];
  onQuote: (variant: Variante | undefined, quantity: number) => void;
}

export default function ProductCard({ product, categoryNames, atasNomes, onQuote }: ProductCardProps) {
  const activeVariant = product.variantes.find((variant) => variant.ativo);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  const changeQty = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(999, prev + delta)));
  };

  const openProductPage = () => {
    navigate(`/produtos/${product.id}`);
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={openProductPage}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openProductPage();
        }
      }}
      className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col min-h-[430px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-donnos-green/40"
    >
      <div className="relative bg-gray-50 p-4 h-40 flex items-center justify-center">
        {product.fotos.length > 0 && (
          <img
            src={product.fotos[0].url}
            alt={product.nome}
            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <span className="text-[10px] font-bold text-donnos-green uppercase tracking-wider bg-donnos-green/10 px-2 py-1 rounded">
            {product.marca}
          </span>
          {categoryNames.slice(0, 1).map((category) => (
            <span key={category} className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {category}
            </span>
          ))}
        </div>

        <h3 className="text-sm font-extrabold text-gray-950 mb-3 leading-snug line-clamp-2 group-hover:text-donnos-green transition-colors">
          {product.nome}
        </h3>

        {atasNomes.length > 0 ? (
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-file-list-3-line text-donnos-green text-xs"></i>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                ATAs Vinculadas
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {atasNomes.slice(0, 3).map((ata) => (
                <span
                  key={ata}
                  className="inline-block text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded"
                >
                  {ata}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
              <i className="ri-information-line"></i>
              Sob consulta
            </span>
          </div>
        )}

        {activeVariant && (
          <p className="mb-4 text-[11px] font-medium text-gray-500">
            A partir de{' '}
            <span className="font-bold text-donnos-green">{activeVariant.estoque} em estoque</span>
          </p>
        )}

        <div className="flex flex-col gap-2 mt-auto">
          <div className="flex w-full items-center justify-center border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                changeQty(-1);
              }}
              disabled={quantity <= 1}
              className="h-8 flex-1 flex items-center justify-center text-gray-500 hover:text-donnos-green hover:bg-gray-50 rounded-l-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Diminuir quantidade"
            >
              <i className="ri-subtract-line text-sm"></i>
            </button>
            <input
              type="text"
              value={quantity}
              readOnly
              onClick={(event) => event.stopPropagation()}
              className="w-12 h-8 text-center text-xs font-semibold text-gray-900 bg-transparent border-x border-gray-200 outline-none"
            />
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                changeQty(1);
              }}
              className="h-8 flex-1 flex items-center justify-center text-gray-500 hover:text-donnos-green hover:bg-gray-50 rounded-r-lg transition-colors cursor-pointer"
              aria-label="Aumentar quantidade"
            >
              <i className="ri-add-line text-sm"></i>
            </button>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onQuote(activeVariant, quantity);
            }}
            className="w-full min-h-9 inline-flex items-center justify-center gap-1.5 bg-donnos-green hover:bg-donnos-green-dark text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors text-center cursor-pointer"
          >
            <i className="ri-chat-quote-line"></i>
            Consultar orçamento
          </button>
        </div>
      </div>
    </article>
  );
}
